import React, { useState, useEffect } from 'react';
import { UserProfile, ProfileChangeRequest } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Send, FileText } from 'lucide-react';

interface ProfileChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserProfile: UserProfile;
  departments: { id: string; name: string }[];
  onRequestSubmitted?: () => void;
}

export default function ProfileChangeRequestModal({
  isOpen,
  onClose,
  currentUserProfile,
  departments,
  onRequestSubmitted
}: ProfileChangeRequestModalProps) {
  const [formData, setFormData] = useState<Partial<UserProfile>>({});
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (currentUserProfile && isOpen) {
      setFormData({
        firstName: currentUserProfile.firstName || '',
        lastName: currentUserProfile.lastName || '',
        cell: currentUserProfile.cell || '',
        sccNumber: currentUserProfile.sccNumber || '',
        sfiEmail: currentUserProfile.sfiEmail || '',
        dob: currentUserProfile.dob || '',
        sfiJoinDate: currentUserProfile.sfiJoinDate || '',
        sfiExpireDate: currentUserProfile.sfiExpireDate || '',
        zavalaJoinDate: currentUserProfile.zavalaJoinDate || '',
        zavalaExpireDate: currentUserProfile.zavalaExpireDate || '',
        department: currentUserProfile.department || '',
        departments: currentUserProfile.departments || (currentUserProfile.department ? [currentUserProfile.department] : []),
        shipName: currentUserProfile.shipName || 'USS Zavala'
      });
      setReason('');
    }
  }, [currentUserProfile, isOpen]);

  const handleDepartmentToggle = (deptName: string) => {
    const current = formData.departments || [];
    let updated: string[];
    if (current.includes(deptName)) {
      updated = current.filter(d => d !== deptName);
    } else {
      updated = [...current, deptName];
    }
    setFormData(prev => ({
      ...prev,
      departments: updated,
      department: updated[0] || ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserProfile?.uid) return;

    // Detect differences
    const requestedChanges: Partial<UserProfile> = {};
    const keysToCheck: (keyof UserProfile)[] = [
      'firstName',
      'lastName',
      'cell',
      'sccNumber',
      'sfiEmail',
      'dob',
      'sfiJoinDate',
      'sfiExpireDate',
      'zavalaJoinDate',
      'zavalaExpireDate',
      'department',
      'departments',
      'shipName'
    ];

    keysToCheck.forEach(key => {
      const currentVal = currentUserProfile[key];
      const newVal = formData[key];
      if (Array.isArray(currentVal) || Array.isArray(newVal)) {
        const arr1 = (currentVal as string[]) || [];
        const arr2 = (newVal as string[]) || [];
        if (arr1.slice().sort().join(',') !== arr2.slice().sort().join(',')) {
          (requestedChanges as any)[key] = newVal;
        }
      } else if ((newVal ?? '') !== (currentVal ?? '')) {
        (requestedChanges as any)[key] = newVal;
      }
    });

    if (Object.keys(requestedChanges).length === 0 && !reason.trim()) {
      toast.info('No changes detected compared to your current personnel file.');
      return;
    }

    setIsSubmitting(true);
    try {
      const newRequest: ProfileChangeRequest = {
        userId: currentUserProfile.uid,
        userEmail: currentUserProfile.email,
        userName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
        currentProfile: {
          firstName: currentUserProfile.firstName || '',
          lastName: currentUserProfile.lastName || '',
          cell: currentUserProfile.cell || '',
          sccNumber: currentUserProfile.sccNumber || '',
          sfiEmail: currentUserProfile.sfiEmail || '',
          dob: currentUserProfile.dob || '',
          sfiJoinDate: currentUserProfile.sfiJoinDate || '',
          sfiExpireDate: currentUserProfile.sfiExpireDate || '',
          zavalaJoinDate: currentUserProfile.zavalaJoinDate || '',
          zavalaExpireDate: currentUserProfile.zavalaExpireDate || '',
          department: currentUserProfile.department || '',
          departments: currentUserProfile.departments || [],
          shipName: currentUserProfile.shipName || ''
        },
        requestedChanges,
        reason: reason.trim(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'profile_change_requests'), newRequest);
      toast.success('Personnel record change request submitted to the XO queue for review.');
      if (onRequestSubmitted) onRequestSubmitted();
      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'profile_change_requests');
      toast.error('Failed to submit change request. Please try again or contact Command.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111] border-2 border-lcars-blue text-[var(--foreground)] font-lcars p-6">
        <DialogHeader className="border-b border-lcars-blue/30 pb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-lcars-gold" />
            <DialogTitle className="text-2xl font-bold text-lcars-gold uppercase tracking-tighter">
              REQUEST PERSONNEL FILE UPDATE
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-[var(--foreground)]/60 font-sans mt-1">
            Submit changes for your profile. Your request will be placed in the Executive Officer (XO) review queue for approval before updating your official record.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Identity Info */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-lcars-blue uppercase tracking-widest border-b border-lcars-blue/20 pb-1">
              Personal & Contact Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">First Name</Label>
                <Input 
                  value={formData.firstName || ''}
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  className="bg-[var(--background)] border-white/20 font-sans uppercase text-sm"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">Last Name</Label>
                <Input 
                  value={formData.lastName || ''}
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  className="bg-[var(--background)] border-white/20 font-sans uppercase text-sm"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">Cell Phone Number</Label>
                <Input 
                  value={formData.cell || ''}
                  onChange={e => setFormData({ ...formData, cell: e.target.value })}
                  placeholder="e.g. 555-123-4567"
                  className="bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">Date of Birth (YYYY-MM-DD)</Label>
                <Input 
                  type="date"
                  value={formData.dob || ''}
                  onChange={e => setFormData({ ...formData, dob: e.target.value })}
                  className="bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>
            </div>
          </div>

          {/* Starfleet & Chapter Info */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-lcars-gold uppercase tracking-widest border-b border-lcars-gold/20 pb-1">
              Starfleet & SFI Registry Data
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">SCC Number</Label>
                <Input 
                  value={formData.sccNumber || ''}
                  onChange={e => setFormData({ ...formData, sccNumber: e.target.value })}
                  placeholder="e.g. 12345"
                  className="bg-[var(--background)] border-white/20 font-sans uppercase text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">SFI Email Address</Label>
                <Input 
                  type="email"
                  value={formData.sfiEmail || ''}
                  onChange={e => setFormData({ ...formData, sfiEmail: e.target.value })}
                  placeholder="officer@sfi.org"
                  className="bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">SFI Expiry Date</Label>
                <Input 
                  type="date"
                  value={formData.sfiExpireDate || ''}
                  onChange={e => setFormData({ ...formData, sfiExpireDate: e.target.value })}
                  className="bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-[var(--foreground)]/70">Zavala Expiry Date</Label>
                <Input 
                  type="date"
                  value={formData.zavalaExpireDate || ''}
                  onChange={e => setFormData({ ...formData, zavalaExpireDate: e.target.value })}
                  className="bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>
            </div>
          </div>

          {/* Department Selection */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-lcars-tan uppercase tracking-widest border-b border-lcars-tan/20 pb-1">
              Assigned Department(s)
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-black/30 p-3 rounded border border-white/10 max-h-40 overflow-y-auto">
              {departments.map(dept => {
                const isSelected = formData.departments?.includes(dept.name);
                return (
                  <label 
                    key={dept.id} 
                    className="flex items-center gap-2 text-xs font-sans cursor-pointer hover:text-lcars-gold transition-colors py-1"
                  >
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={() => handleDepartmentToggle(dept.name)}
                    />
                    <span className="truncate">{dept.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Explanation / Notes for XO */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-lcars-gold font-bold">
              Reason for Request / Note to XO (Optional)
            </Label>
            <Textarea 
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g., Renewed my SFI membership through 2027, updated my phone number, requested transfer to Engineering."
              rows={3}
              className="bg-[var(--background)] border-white/20 font-sans text-sm normal-case"
            />
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4 border-t border-white/10">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-white/20 uppercase tracking-widest text-xs"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-lcars-gold hover:bg-lcars-gold/80 text-black font-bold uppercase tracking-widest text-xs flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSubmitting ? 'SUBMITTING TO XO...' : 'SUBMIT REQUEST TO XO'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
